import { useEffect } from 'react';
import { Toaster as SonnerToaster, toast as sonner } from 'sonner';
import { useUIStore, type Toast } from '@/stores/uiStore';

/**
 * Renders the toasts the app has been raising all along.
 *
 * `uiStore.addToast` is called from ~180 places — every "Saved", every
 * "Could not update…" — and until this component nothing drew them: the store
 * collected each message, timed it out, and threw it away unseen. A failed
 * save looked exactly like a successful one. Several newer screens say so in
 * their own comments and confirm inline instead.
 *
 * This is the missing half. It watches the store and hands each NEW toast to
 * Sonner (the library Ember uses), which owns stacking, timing, swipe-to-
 * dismiss and the `aria-live` announcement. Call sites do not change: the
 * store's API is the API.
 *
 * Mounted once, at the root, so a toast raised on a public page or in the
 * researcher portal is shown too.
 */

const DEFAULT_DURATION_MS = 5000;
/** Errors stay up longer: they are the ones that need reading. */
const ERROR_DURATION_MS = 8000;

function durationFor(toast: Toast): number {
  if (toast.duration === undefined) {
    return toast.type === 'error' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS;
  }
  // The store reads a non-positive duration as "do not time this out".
  return toast.duration > 0 ? toast.duration : Infinity;
}

function show(toast: Toast) {
  const options = {
    id: toast.id,
    description: toast.message,
    duration: durationFor(toast),
  };
  switch (toast.type) {
    case 'success':
      sonner.success(toast.title, options);
      break;
    case 'error':
      sonner.error(toast.title, options);
      break;
    case 'warning':
      sonner.warning(toast.title, options);
      break;
    default:
      sonner.info(toast.title, options);
  }
}

export function Toaster() {
  const theme = useUIStore((s) => s.theme);

  useEffect(() => {
    // Subscribe rather than render from `toasts`: the store removes a toast on
    // its own timer, and mirroring that array would make two clocks fight over
    // when a message disappears. Each toast is handed over once, by id.
    const seen = new Set<string>();
    return useUIStore.subscribe((state) => {
      for (const toast of state.toasts) {
        if (seen.has(toast.id)) continue;
        seen.add(toast.id);
        show(toast);
      }
      // Forget ids the store has dropped, so the set cannot grow for the life
      // of the tab.
      if (seen.size > state.toasts.length) {
        const live = new Set(state.toasts.map((t) => t.id));
        for (const id of seen) if (!live.has(id)) seen.delete(id);
      }
    });
  }, []);

  return (
    <SonnerToaster
      theme={theme}
      position="bottom-right"
      richColors
      closeButton
      expand={false}
      visibleToasts={4}
      gap={10}
      offset={20}
      toastOptions={{
        style: { fontFamily: 'inherit' },
        classNames: {
          toast: 'rounded-xl shadow-elevated text-sm',
          title: 'font-medium',
          description: 'text-[13px] opacity-90',
        },
      }}
    />
  );
}
